// @ts-nocheck
/*
 * EXALT Product (CTR)
 * - Alpine controller for the Subscribe / One-time buy box
 * - Swiper init for the product gallery (main + thumbnails)
 */

/* ------------------------------------------------------------------ */
/* Alpine buy-box controller                                           */
/* ------------------------------------------------------------------ */
document.addEventListener('alpine:init', () => {
  Alpine.data('ctrExaltProduct', (sectionId) => ({
    cfg: null,
    purchaseType: 'subscription',
    selectedPack: 0,
    currentSellingPlanId: null,
    loading: false,
    stickyVisible: false,

    init() {
      const node = this.$el.querySelector('[data-exalt-config]');
      try {
        this.cfg = JSON.parse(node.textContent);
      } catch (e) {
        this.cfg = { packs: [], plans: [], variantPrice: 0 };
        console.error('[ctr-exalt-product] bad config', e);
      }

      this.purchaseType = this.cfg.hasSellingPlans ? 'subscription' : 'one-time';
      this.currentSellingPlanId = this.cfg.defaultSellingPlanId || null;

      // Default to the first pack that has a subscription discount + detail box
      // (matches the "Most popular" 5-bottle in the design), else the first pack.
      const idx = this.cfg.packs.findIndex((p) => p.discount && p.showDetails);
      this.selectedPack = idx > -1 ? idx : 0;

      // Show the sticky mobile bar once the main ATC button scrolls out of view.
      this.$nextTick(() => {
        const target = this.$refs.mainAtc;
        if (!target || typeof IntersectionObserver === 'undefined') return;
        this._io = new IntersectionObserver(
          ([entry]) => {
            this.stickyVisible = !entry.isIntersecting;
          },
          { rootMargin: '0px 0px -10% 0px' }
        );
        this._io.observe(target);
      });
    },

    destroy() {
      if (this._io) this._io.disconnect();
    },

    /* ----- pack helpers (all amounts in cents) ----- */
    pack(idx) {
      return (this.cfg && this.cfg.packs[idx]) || { qty: 1, discount: false };
    },

    packFull(idx) {
      return this.pack(idx).qty * this.cfg.variantPrice;
    },

    packSubscribe(idx) {
      const full = this.packFull(idx);
      if (!this.pack(idx).discount) return full;
      return Math.round(full * (1 - (this.cfg.discountPercent || 0) / 100));
    },

    /* ----- display strings ----- */
    packMainPrice(idx) {
      return this.money(this.purchaseType === 'subscription' ? this.packSubscribe(idx) : this.packFull(idx));
    },

    packComparePrice(idx) {
      if (this.purchaseType === 'subscription') {
        return this.pack(idx).discount ? this.money(this.packFull(idx)) : '';
      }
      // one-time: only strike if the product itself has a compare-at price
      if (this.cfg.variantCompare > 0) {
        return this.money(this.pack(idx).qty * this.cfg.variantCompare);
      }
      return '';
    },

    packPerUnit(idx) {
      const qty = this.pack(idx).qty || 1;
      const base = this.purchaseType === 'subscription' ? this.packSubscribe(idx) : this.packFull(idx);
      return `${this.money(Math.round(base / qty))} / ${this.cfg.perUnitLabel || 'bottle'}`;
    },

    /* ----- split price-line helpers: "£39 | £3.25 per bottle" ----- */
    moneyTrim(cents) {
      try {
        const whole = cents % 100 === 0;
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: this.cfg.currency || 'GBP',
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2,
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    },

    packPriceMain(idx) {
      const base = this.purchaseType === 'subscription' ? this.packSubscribe(idx) : this.packFull(idx);
      return this.moneyTrim(base);
    },

    packPricePer(idx) {
      const qty = this.pack(idx).qty || 1;
      const base = this.purchaseType === 'subscription' ? this.packSubscribe(idx) : this.packFull(idx);
      return this.moneyTrim(Math.round(base / qty));
    },

    packCompareMain(idx) {
      if (this.purchaseType === 'subscription') {
        if (!this.pack(idx).discount) return '';
        return this.moneyTrim(this.packFull(idx));
      }
      if (!(this.cfg.variantCompare > 0)) return '';
      return this.moneyTrim(this.pack(idx).qty * this.cfg.variantCompare);
    },

    packComparePer(idx) {
      const qty = this.pack(idx).qty || 1;
      if (this.purchaseType === 'subscription') {
        if (!this.pack(idx).discount) return '';
        return this.moneyTrim(Math.round(this.packFull(idx) / qty));
      }
      if (!(this.cfg.variantCompare > 0)) return '';
      return this.moneyTrim(Math.round((this.pack(idx).qty * this.cfg.variantCompare) / qty));
    },

    // First order is the discounted (e.g. 10% off) price; recurring reverts to full.
    packFirstOrder(idx) {
      return this.money(this.packSubscribe(idx));
    },

    packAfterOrder(idx) {
      return this.money(this.packFull(idx));
    },

    packSave(idx) {
      const diff = this.packFull(idx) - this.packSubscribe(idx);
      return diff > 0 ? this.money(diff) : '';
    },

    /* ----- button + ship line ----- */
    get buttonLabel() {
      const idx = this.selectedPack;
      if (this.purchaseType === 'one-time') {
        return `Buy now • ${this.money(this.packFull(idx))}`;
      }
      const full = this.money(this.packFull(idx));
      if (this.pack(idx).discount) {
        return `Subscribe · ${this.money(this.packSubscribe(idx))} today`.toUpperCase();
      }
      return `Subscribe · ${full}`.toUpperCase();
    },

    get stickyLabel() {
      const price = this.purchaseType === 'subscription'
        ? this.packSubscribe(this.selectedPack)
        : this.packFull(this.selectedPack);
      return `${this.cfg.stickyPrefix || 'Buy now'} · ${this.money(price)}`;
    },

    get nextDayLabel() {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      const parts = new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).formatToParts(d);
      const get = (t) => (parts.find((p) => p.type === t) || {}).value || '';
      return `${get('weekday')} ${get('day')} ${get('month')}`;
    },

    /* ----- interactions ----- */
    selectPack(idx) {
      this.selectedPack = idx;
    },

    onChangeFrequency(event) {
      this.currentSellingPlanId = parseInt(event.target.value, 10);
    },

    money(cents) {
      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: this.cfg.currency || 'GBP',
          minimumFractionDigits: 2,
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    },

    async addToCart() {
      if (this.loading) return;
      this.loading = true;

      const body = {
        id: this.cfg.variantId,
        quantity: this.pack(this.selectedPack).qty,
      };
      if (this.purchaseType === 'subscription' && this.currentSellingPlanId) {
        body.selling_plan = this.currentSellingPlanId;
      }

      // Line-item property so discount apps (e.g. Discount Kit) can target these.
      const lp = this.cfg.liProp || {};
      const inScope = lp.scope === 'always' || lp.scope === this.purchaseType;
      if (lp.enable && inScope) {
        const props = {};
        if (lp.key) props[lp.key] = lp.value;
        if (lp.includePack) props[lp.packKey || 'Pack'] = this.pack(this.selectedPack).title;
        if (Object.keys(props).length) body.properties = props;
      }

      // R-TAP (17 Sep 2026). THIS PATH WAS THE LAST ONE STILL HAND-ROLLING ITS
      // OWN ADD, and routing it through cartController.add() was the cheapest
      // way to get the drawer open on the tap. Three things came with it, and
      // the first is not cosmetic:
      //
      // 1. **A SUBSCRIPTION BOUGHT HERE CARRIED NO DYNAMIC BOX PROPERTIES.**
      //    The body built above sets `selling_plan` and stops. Every other add
      //    path in the theme goes through withDynamicBox(), which waits for the
      //    store's dynamicBoxRequest and stamps `_dynamicBoxId` and
      //    `_dynamicBoxIndex`, and refuses outright when that data is missing -
      //    cart-controller.js says in as many words that a boxless subscription
      //    is worse than no subscription. This file bypassed the guard.
      //    The four plan ids this section is configured with in
      //    templates/product.json:181 and templates/product.protein.json:122
      //    are exactly DYNAMIC_BOX_PLAN_IDS in cart-controller.js:34, so the
      //    guard accepts them and nothing legitimate is newly refused.
      // 2. No failure here reached the customer. `console.error` was the whole
      //    error surface: Shopify refusing the add, a 429 with an HTML body, a
      //    dropped connection - all silent, with the button re-enabling as if
      //    it had worked. The controller surfaces every one of them once.
      // 3. It morphed sections but never published an item count, so the badge
      //    and every non-morphing surface went unnotified; and it asked only
      //    the cart-items-components present in THIS document rather than the
      //    registry. applyMutation() does both properly.
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      cartDrawer?.openForPendingAdd?.();

      try {
        if (!window.cartController?.add) {
          const error = new Error('cart-controller.js is not loaded; no cart request was sent');
          console.error('[ctr-exalt-product]', error.message);
          window.ctrNotifyCartFailure?.(
            'Sorry, your basket could not be updated. Please refresh the page and try again.',
            'ctr-exalt-product',
            error
          );
          return;
        }

        const result = await window.cartController.add(body, { source: 'ctr-exalt-product' });

        // The controller has already surfaced anything that failed. Saying it
        // again here is the R90b duplicate-message bug.
        if (!result.ok) return;

        cartDrawer?.open?.();
      } catch (err) {
        console.error('[ctr-exalt-product] add to cart failed:', err);
        window.ctrNotifyCartFailure?.(
          'Sorry, your basket could not be updated. Please try again.',
          'ctr-exalt-product',
          err
        );
      } finally {
        cartDrawer?.clearPendingAdd?.();
        this.loading = false;
      }
    },
  }));
});

/* ------------------------------------------------------------------ */
/* Swiper gallery                                                      */
/* ------------------------------------------------------------------ */
function ctrExaltInitGalleries(root) {
  const scope = root || document;
  if (typeof Swiper === 'undefined') return;

  scope.querySelectorAll('[data-exalt-gallery]').forEach((gallery) => {
    if (gallery.dataset.inited === 'true') return;
    gallery.dataset.inited = 'true';

    const mainEl = gallery.querySelector('.exalt-gallery__main');
    const thumbsEl = gallery.querySelector('.exalt-gallery__thumbs');
    if (!mainEl) return;

    let thumbs = null;
    const shared = {
      observer: true,
      observeParents: true,
      resizeObserver: true,
    };

    if (thumbsEl) {
      thumbs = new Swiper(thumbsEl, {
        ...shared,
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
      });
    }

    // eslint-disable-next-line no-new
    const main = new Swiper(mainEl, {
      ...shared,
      slidesPerView: 1,
      spaceBetween: 10,
      thumbs: thumbs ? { swiper: thumbs } : undefined,
      /*
        SWIPE MUST STILL WORK OVER A LOADED VIDEO. Codex UAT, 2 Sep 2026, mobile 390x844:
        once the video on slide 4 was playing, swiping left did nothing - the slide would not
        change, the video kept advancing, and because slideChange never fired the pause handler
        below never ran either. The customer was trapped on a playing video.

        Swiper's `formElements` default is 'input, select, option, textarea, button, video, label'.
        A drag STARTING on one of those is deliberately handed to the browser rather than treated
        as a swipe - sensible for a text field, wrong for a full-slide play control. The
        deferred-media interaction hint is a <button> sized to the whole slide, so it covered the
        entire drag surface.

        `touchStartForcePreventDefault` restores the drag on those elements. Tap-to-play and
        tap-to-pause still work: a press with no movement still produces a click, which is what
        `on:click="/toggleMedia"` listens for.

        Scoped to this gallery's Swiper instance only. The thumbnail slider above is untouched.
      */
      touchStartForcePreventDefault: true,
    });
    gallery._swiper = main;

    // Grid/flex parents can report 0 width at init — recalc once layout settles.
    requestAnimationFrame(() => {
      main.update();
      if (thumbs) thumbs.update();
    });

    /* --------------------------------------------------------------
       PAUSE VIDEO WHEN THE CUSTOMER LEAVES ITS SLIDE. 2 Sep 2026.

       Video became playable on this gallery in the same change that added this.
       Without it, swiping away from a playing video leaves it running: the
       customer hears audio from a slide they can no longer see, and on mobile
       that is the whole screen away.

       Bound to Swiper's slideChange, which fires for a swipe, an arrow, a
       thumbnail click and a programmatic goTo alike - so there is no path off
       the slide that skips it.

       Pauses every video in the gallery rather than tracking which one was
       playing: cheaper, and it cannot leave one running if two ever load. The
       poster button is restored so the slide reads as playable again, matching
       what DeferredMedia does on its own error path.
       -------------------------------------------------------------- */
    main.on('slideChange', () => {
      // Delegate to the component's own pauseMedia(), assets/media.js:115. It postMessages a
      // pause to YouTube/Vimeo (their URLs already carry enablejsapi / api), pauses a native
      // <video>, clears isPlaying and updates the play/pause hint.
      //
      // An earlier version of this handler hand-rolled the pause and Codex round 3 rejected it
      // as a P1, correctly on both counts. It called video.pause() directly, which left the
      // component's isPlaying true so the pause control stayed exposed on stopped media and
      // needed an extra tap to resume. And it "stopped" cross-origin embeds by reassigning
      // frame.src - but snippets/video.liquid:125,131 build those URLs with autoplay: true, so
      // reloading RESTARTS them from the beginning, playing. That was worse than doing nothing.
      gallery.querySelectorAll('deferred-media').forEach((host) => {
        if (typeof host.pauseMedia === 'function') host.pauseMedia();
      });
    });

    /* --------------------------------------------------------------
       RESYNC WHEN A VIDEO ENDS ON ITS OWN. Codex round 6, P2.

       assets/media.js registers no native play/pause/ended listeners, so its
       `isPlaying` flag only tracks transitions the component itself performs.
       This caller passes no `video_loop`, so a video can reach its natural end -
       at which point the browser fires `ended`, `isPlaying` stays true, the play
       icon stays hidden, and the CSS rule added alongside this leaves the
       full-slide control at opacity 0.

       The customer then sees a finished video with no visible way to replay it,
       and their first tap calls pauseMedia() because of the stale flag - so it
       takes TWO taps to restart. That is a defect this change introduced the
       surface for, so it is fixed here rather than left.

       `ended` does not bubble, but the capture phase reaches ancestors for every
       event regardless, so one listener on the gallery covers every video
       including ones hydrated later.
       -------------------------------------------------------------- */
    gallery.addEventListener(
      'ended',
      (event) => {
        const host = event.target?.closest?.('deferred-media');
        if (host && typeof host.pauseMedia === 'function') host.pauseMedia();
      },
      true
    );

    /* --------------------------------------------------------------
       DEFERRED GALLERY SLIDES, re-landed 2 Sep 2026.

       sections/ctr-exalt-product.liquid renders slides 3..n with their
       src/srcset held in data-ctr-src / data-ctr-srcset, so they do not
       fetch on page load. Measured on live: 555,716 B of a 905 KB image
       payload was gallery slides nobody had swiped to.

       The <img> keeps its width, height, class and alt, so Swiper's
       geometry and the thumbnail pairing are unaffected - which is why
       this uses data- attributes rather than the <template> approach the
       carousels use. Swiper here initialises on DOMContentLoaded and
       measures every slide.

       IT APPLIES TO GALLERY IMAGES ONLY, NEVER TO A VIDEO POSTER AND
       NEVER TO THE NATIVE GALLERY. The first attempt was reverted the
       same day (6266c20) after a follow-up commit extended the same
       mechanism to both. The reason 6266c20 gave - that posters carry no
       width/height - is FALSE; image_tag emits both. The real cause is
       NOT established. See the long comment in the Liquid before
       widening the scope.

       HYDRATION IS ON FIRST INTENT, NOT ON A TIMER, for slides 3+.
       Anything that could lead to slide 3 being shown restores every
       deferred image at once: a pointer or touch on the gallery, a
       keyboard arrow, a thumbnail click, or any Swiper slide change.
       Slides 3+ are deliberately NOT hydrated on an idle callback: that
       would spend the bytes for every visitor including the ones who
       never touch the gallery, which is the entire saving.

       SLIDE 2 IS THE EXCEPTION (R60, 7 Sep 2026, Charlie's call: "defer
       until last minute possible but not as to impact CX"). It is the
       largest file on the page and was eager, fetched at ~3.3 s for a
       swipe most visitors never make. It now carries data-ctr-slide2 and
       is hydrated AFTER the window load event, on idle - off the hero's
       critical path, but in flight before almost any swipe, so the first
       swipe still lands on a real image. A swipe before that fires the
       intent path above, which hydrates everything including slide 2.
       Skipped under Save-Data; intent still covers it.
       -------------------------------------------------------------- */
    const deferredImgs = () => gallery.querySelectorAll('img[data-ctr-defer]');
    let galleryHydrated = false;
    const hydrateGallery = () => {
      if (galleryHydrated) return;
      galleryHydrated = true;
      deferredImgs().forEach((img) => {
        const srcset = img.getAttribute('data-ctr-srcset');
        const src = img.getAttribute('data-ctr-src');
        if (srcset) img.setAttribute('srcset', srcset);
        if (src) img.setAttribute('src', src);
        img.removeAttribute('data-ctr-srcset');
        img.removeAttribute('data-ctr-src');
        img.removeAttribute('data-ctr-defer');
      });
      main.update();
      if (thumbs) thumbs.update();
    };

    const hydrateOne = (img) => {
      const srcset = img.getAttribute('data-ctr-srcset');
      const src = img.getAttribute('data-ctr-src');
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
      img.removeAttribute('data-ctr-srcset');
      img.removeAttribute('data-ctr-src');
      img.removeAttribute('data-ctr-defer');
      img.removeAttribute('data-ctr-slide2');
    };
    const warmSlide2 = () => {
      const conn = navigator.connection;
      if (conn && conn.saveData) return;
      const run = () => gallery.querySelectorAll('img[data-ctr-slide2]').forEach(hydrateOne);
      const idle = () => {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 4000 });
        else setTimeout(run, 1500);
      };
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    };

    if (deferredImgs().length) {
      warmSlide2();
      const intentOpts = { passive: true, once: true };
      ['pointerdown', 'touchstart', 'mouseenter', 'keydown'].forEach((evt) => {
        gallery.addEventListener(evt, hydrateGallery, intentOpts);
      });
      // Any programmatic or gesture-driven slide change, including a thumb click.
      main.on('slideChange', hydrateGallery);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => ctrExaltInitGalleries());
// Theme editor: re-init when this section is (re)loaded
document.addEventListener('shopify:section:load', (e) => ctrExaltInitGalleries(e.target));
